/**
 * Tab de Deudas y Financiación
 * Soporta tres productos: Click & Pay, Préstamo, Línea de Crédito.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Plus, RefreshCw, Trash2, Zap, TrendingUp, Banknote, Link2,
  Calendar, Check, Clock, History, Users, Pencil, RotateCcw, AlertTriangle, AlertCircle, Activity,
  CheckCircle2, Ban
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { API_URL, formatNumber } from './finanzasUtils';

const TIPO_META = {
  click_pay: {
    label: 'Click & Pay',
    icon: Zap,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  prestamo: {
    label: 'Préstamo',
    icon: CreditCard,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  linea_credito: {
    label: 'Línea de Crédito',
    icon: TrendingUp,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  prestamo_socio: {
    label: 'Préstamo Socio',
    icon: Users,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
};

const TIPO_MOV_LABEL = {
  desembolso: '💰 Desembolso',
  cuota_capital: '🔁 Cuota capital',
  comision: '💸 Comisión',
  interes: '📈 Interés',
};

const NOMBRES_MES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

const PrestamosTab = ({ headers }) => {
  const [operaciones, setOperaciones] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [pagosMes, setPagosMes] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [verCerradas, setVerCerradas] = useState(false);
  const [showNueva, setShowNueva] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detalleId, setDetalleId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cuotasDetalle, setCuotasDetalle] = useState(null);

  const initForm = {
    tipo: 'click_pay',
    nombre: '',
    entidad: 'BBVA',
    fecha_inicio: new Date().toISOString().slice(0, 10),
    importe_solicitado: '',
    num_cuotas: '',
    importe_cuota: '',
    cuotas_pagadas: '0',
    comision: '',
    tipo_interes: '',
    limite: '',
    dispuesto: '',
    socio_nombre: '',
    notas: '',
  };
  const [form, setForm] = useState(initForm);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [resOps, resPagos] = await Promise.all([
        fetch(`${API_URL}/api/finanzas/operaciones-financiacion${verCerradas ? '?incluir_cerradas=true' : ''}`, { headers }),
        fetch(`${API_URL}/api/finanzas/operaciones-financiacion/pagos-mes`, { headers }),
      ]);
      const data = await resOps.json();
      setOperaciones(data.operaciones || []);
      setResumen(data.resumen || null);
      if (resPagos.ok) {
        const pagos = await resPagos.json();
        setPagosMes(pagos);
      }
    } catch (e) {
      console.error('Error cargando operaciones:', e);
    } finally {
      setCargando(false);
    }
  }, [headers, verCerradas]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDetalle = async (id) => {
    setDetalleId(id);
    setDetalle(null);
    setCuotasDetalle(null);
    try {
      const [resDetalle, resCuotas] = await Promise.all([
        fetch(`${API_URL}/api/finanzas/operaciones-financiacion/${id}`, { headers }),
        fetch(`${API_URL}/api/finanzas/operaciones-financiacion/${id}/cuotas`, { headers }),
      ]);
      const data = await resDetalle.json();
      const cuotasData = await resCuotas.json();
      setDetalle(data);
      setCuotasDetalle(cuotasData);
    } catch (e) {
      console.error('Error cargando detalle:', e);
    }
  };

  const cerrarModal = () => {
    setShowNueva(false);
    setEditingId(null);
    setForm(initForm);
  };

  const abrirEditar = (op) => {
    setEditingId(op.id);
    setForm({
      tipo: op.tipo || 'click_pay',
      nombre: op.nombre || '',
      entidad: op.entidad || 'BBVA',
      fecha_inicio: op.fecha_inicio || new Date().toISOString().slice(0, 10),
      importe_solicitado: op.importe_solicitado != null ? String(op.importe_solicitado) : '',
      num_cuotas: op.num_cuotas != null ? String(op.num_cuotas) : '',
      importe_cuota: op.importe_cuota != null ? String(op.importe_cuota) : '',
      cuotas_pagadas: op.cuotas_pagadas != null ? String(op.cuotas_pagadas) : '0',
      comision: op.comision != null ? String(op.comision) : '',
      tipo_interes: op.tipo_interes != null ? String(op.tipo_interes) : '',
      limite: op.limite != null ? String(op.limite) : '',
      dispuesto: op.dispuesto != null ? String(op.dispuesto) : '',
      socio_nombre: op.socio_nombre || '',
      notas: op.notas || '',
    });
    setShowNueva(true);
  };

  const guardar = async () => {
    try {
      const body = {
        tipo: form.tipo,
        nombre: form.nombre,
        entidad: form.entidad || 'BBVA',
        fecha_inicio: form.fecha_inicio || null,
        notas: form.notas || null,
        importe_solicitado: parseFloat(form.importe_solicitado) || 0,
        num_cuotas: form.num_cuotas ? parseInt(form.num_cuotas) : null,
        importe_cuota: form.importe_cuota ? parseFloat(form.importe_cuota) : null,
        cuotas_pagadas: parseInt(form.cuotas_pagadas) || 0,
        comision: parseFloat(form.comision) || 0,
        tipo_interes: parseFloat(form.tipo_interes) || 0,
        limite: form.limite ? parseFloat(form.limite) : null,
        dispuesto: parseFloat(form.dispuesto) || 0,
        socio_nombre: form.socio_nombre || null,
      };
      const url = editingId
        ? `${API_URL}/api/finanzas/operaciones-financiacion/${editingId}`
        : `${API_URL}/api/finanzas/operaciones-financiacion`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (editingId && typeof data.cuotas_regeneradas === 'number' && data.cuotas_regeneradas > 0) {
          // Refrescar detalle expandido si era el que estábamos editando
          if (detalleId === editingId) abrirDetalle(editingId);
        }
        cerrarModal();
        cargar();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || (editingId ? 'Error actualizando operación' : 'Error creando operación'));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta operación? Los cargos vinculados se desvincularán.')) return;
    try {
      const res = await fetch(`${API_URL}/api/finanzas/operaciones-financiacion/${id}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        if (detalleId === id) { setDetalleId(null); setDetalle(null); }
        cargar();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const desvincular = async (vinculoId) => {
    if (!window.confirm('¿Desvincular este cargo de la operación?')) return;
    try {
      const res = await fetch(`${API_URL}/api/finanzas/operaciones-financiacion/vinculos/${vinculoId}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        abrirDetalle(detalleId);
        cargar();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const marcarSaldada = async (op) => {
    const r = op.resumen || {};
    const pendiente = Number(r.deuda_capital || 0);
    const aviso = pendiente > 0
      ? `Quedan ${formatNumber(pendiente)} sin vincular a ningún movimiento bancario. `
      : '';
    if (!window.confirm(
      `¿Marcar "${op.nombre}" como saldada?\n\n${aviso}` +
      'La deuda pasará a 0 y dejará de contar en los totales. Las cuotas futuras se anularán. ' +
      'Podrás reabrirla más adelante si hace falta.'
    )) return;
    try {
      const res = await fetch(`${API_URL}/api/finanzas/operaciones-financiacion/${op.id}/cerrar`, {
        method: 'POST', headers
      });
      if (res.ok) {
        if (detalleId === op.id) { setDetalleId(null); setDetalle(null); setCuotasDetalle(null); }
        cargar();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Error al marcar como saldada');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const reabrir = async (id) => {
    if (!window.confirm('¿Reabrir esta operación para permitir nuevas vinculaciones?')) return;
    try {
      const res = await fetch(`${API_URL}/api/finanzas/operaciones-financiacion/${id}/reabrir`, {
        method: 'POST', headers
      });
      if (res.ok) {
        if (detalleId === id) abrirDetalle(id);
        cargar();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Error al reabrir');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const renderCampoEspecifico = () => {
    if (form.tipo === 'prestamo_socio') {
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Socio *</label>
            <select
              value={form.socio_nombre}
              onChange={(e) => setForm({ ...form, socio_nombre: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Selecciona un socio…</option>
              <option value="Manu">Manu</option>
              <option value="Alfonso">Alfonso</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Vincular a un socio fijo evita duplicidades al desglosar la deuda con socios.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium">Importe prestado (€) *</label>
            <Input type="number" value={form.importe_solicitado}
              onChange={(e) => setForm({ ...form, importe_solicitado: e.target.value })}
              placeholder="5000" />
            <p className="text-xs text-gray-500 mt-1">
              Préstamo sin intereses. La devolución es libre — la registras vinculando cargos bancarios como &quot;Devolución a socio&quot;.
            </p>
          </div>
        </div>
      );
    }
    if (form.tipo === 'linea_credito') {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Límite de crédito (€) *</label>
            <Input type="number" value={form.limite}
              onChange={(e) => setForm({ ...form, limite: e.target.value })}
              placeholder="25000" />
          </div>
          <div>
            <label className="text-sm font-medium">Dispuesto actual (€)</label>
            <Input type="number" value={form.dispuesto}
              onChange={(e) => setForm({ ...form, dispuesto: e.target.value })}
              placeholder="0" />
          </div>
          <div>
            <label className="text-sm font-medium">TIN Anual (%)</label>
            <Input type="number" step="0.01" value={form.tipo_interes}
              onChange={(e) => setForm({ ...form, tipo_interes: e.target.value })}
              placeholder="7.5" />
          </div>
        </div>
      );
    }
    // click_pay y prestamo comparten estructura
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Importe solicitado (€) *</label>
          <Input type="number" value={form.importe_solicitado}
            onChange={(e) => setForm({ ...form, importe_solicitado: e.target.value })}
            placeholder="5000" />
        </div>
        <div>
          <label className="text-sm font-medium">Nº cuotas *</label>
          <Input type="number" value={form.num_cuotas}
            onChange={(e) => setForm({ ...form, num_cuotas: e.target.value })}
            placeholder={form.tipo === 'click_pay' ? '3' : '12'} />
        </div>
        <div>
          <label className="text-sm font-medium">Cuota mensual (€)</label>
          <Input type="number" value={form.importe_cuota}
            onChange={(e) => setForm({ ...form, importe_cuota: e.target.value })}
            placeholder="Auto-calculada" />
        </div>
        <div>
          <label className="text-sm font-medium">
            {form.tipo === 'click_pay' ? 'Comisión fija (€)' : 'Comisión apertura (€)'}
          </label>
          <Input type="number" value={form.comision}
            onChange={(e) => setForm({ ...form, comision: e.target.value })}
            placeholder="50" />
        </div>
        {form.tipo === 'prestamo' && (
          <div className="col-span-2">
            <label className="text-sm font-medium">TIN Anual (%)</label>
            <Input type="number" step="0.01" value={form.tipo_interes}
              onChange={(e) => setForm({ ...form, tipo_interes: e.target.value })}
              placeholder="5.5" />
          </div>
        )}
        <div className="col-span-2">
          <label className="text-sm font-medium">Cuotas ya pagadas (legacy)</label>
          <Input type="number" min="0" value={form.cuotas_pagadas}
            onChange={(e) => setForm({ ...form, cuotas_pagadas: e.target.value })}
            placeholder="0" />
          <p className="text-xs text-gray-500 mt-1">
            Si ya llevas cuotas pagadas del año pasado sin registrar aquí, indícalas. El calendario empezará en la cuota siguiente.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="w-5 h-5 text-red-500" />
              Deudas y Financiación
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Click & Pay, préstamos y línea de crédito. Vincula cargos bancarios para ver la deuda real.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVerCerradas(v => !v)}
              className={verCerradas ? 'bg-gray-100' : ''}
              title="Mostrar también las operaciones ya saldadas (cerradas)"
            >
              <History className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">{verCerradas ? 'Ocultar pagadas' : 'Ver pagadas'}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={cargar}>
              <RefreshCw className={`w-4 h-4 ${cargando ? 'animate-spin' : ''}`} />
              <span className="ml-1 hidden sm:inline">Actualizar</span>
            </Button>
            <Button size="sm" onClick={() => setShowNueva(true)}>
              <Plus className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">Nueva operación</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {resumen && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-red-600">Operaciones</p>
                <p className="text-xl font-bold text-red-700">{resumen.num_operaciones}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-xs text-orange-600">Deuda real</p>
                <p className="text-xl font-bold text-orange-700">{formatNumber(resumen.deuda_total)}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-xs text-purple-600">Coste financiero</p>
                <p className="text-xl font-bold text-purple-700">{formatNumber(resumen.coste_financiero_total)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600">Línea disponible</p>
                <p className="text-xl font-bold text-blue-700">{formatNumber(resumen.linea_credito_disponible)}</p>
              </div>
            </div>
          )}

          {pagosMes && pagosMes.pagos && pagosMes.pagos.length > 0 && (
            <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  Pagos de {NOMBRES_MES[(pagosMes.mes || 1) - 1]} {pagosMes.anio}
                </p>
                <div className="flex gap-3 text-xs">
                  {pagosMes.pendiente_mes > 0 && (
                    <span className="text-orange-700">
                      Pendiente: <strong>{formatNumber(pagosMes.pendiente_mes)}</strong>
                    </span>
                  )}
                  {pagosMes.pagado_mes > 0 && (
                    <span className="text-green-700">
                      Pagado: <strong>{formatNumber(pagosMes.pagado_mes)}</strong>
                    </span>
                  )}
                  <span className="text-indigo-900">
                    Total: <strong>{formatNumber(pagosMes.total_mes)}</strong>
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                {pagosMes.pagos.map((p) => {
                  const meta = TIPO_META[p.tipo] || TIPO_META.prestamo;
                  const pagada = p.estado === 'pagada';
                  return (
                    <div
                      key={p.id}
                      className={`flex items-center justify-between text-sm py-1.5 px-2 rounded ${pagada ? 'bg-green-50' : 'bg-white border border-indigo-100'}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {pagada ? (
                          <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
                        ) : (
                          <Clock className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                        )}
                        <span className="font-medium text-indigo-900 shrink-0">
                          Día {p.dia ?? '—'}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full bg-white border ${meta.border} ${meta.color} shrink-0`}>
                          {meta.label}
                        </span>
                        <span className="text-gray-700 truncate">
                          {p.operacion_nombre}
                          {p.numero_cuota ? ` · cuota ${p.numero_cuota}` : ''}
                        </span>
                      </div>
                      <span className={`font-semibold ${pagada ? 'text-green-700' : 'text-gray-900'}`}>
                        {formatNumber(p.importe)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {operaciones.length === 0 && !cargando ? (
            <div className="text-center py-8">
              <Banknote className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">No hay operaciones de financiación</p>
              <p className="text-sm text-gray-500 mb-4">
                Añade tu Click & Pay, préstamo o línea de crédito BBVA para empezar
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {operaciones.map(op => {
                const meta = TIPO_META[op.tipo] || TIPO_META.prestamo;
                const Icon = meta.icon;
                const r = op.resumen || {};
                const expandido = detalleId === op.id;
                return (
                  <div key={op.id} className={`rounded-lg border ${meta.border} ${meta.bg} ${op.estado === 'cerrada' ? 'opacity-70' : ''}`}>
                    <div
                      className="p-4 cursor-pointer"
                      onClick={() => expandido ? (setDetalleId(null), setDetalle(null), setCuotasDetalle(null)) : abrirDetalle(op.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          <Icon className={`w-5 h-5 mt-0.5 ${meta.color}`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{op.nombre}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full bg-white ${meta.color} border ${meta.border}`}>
                                {meta.label}
                              </span>
                              {op.estado === 'cerrada' && (
                                <span
                                  className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600"
                                  title={op.cierre_manual
                                    ? `Marcada como saldada a mano${op.fecha_cierre ? ` el ${op.fecha_cierre}` : ''}${r.deuda_capital_teorica > 0 ? ` (quedaban ${formatNumber(r.deuda_capital_teorica)} sin vincular)` : ''}`
                                    : 'Saldada: todo lo prestado está devuelto'}
                                >
                                  {op.cierre_manual ? 'Saldada a mano' : 'Saldada'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{op.entidad}{op.fecha_inicio ? ` · ${op.fecha_inicio}` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {op.estado === 'cerrada' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600 hover:bg-emerald-100"
                              onClick={(e) => { e.stopPropagation(); reabrir(op.id); }}
                              title="Reabrir operación"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600 hover:bg-emerald-100"
                              onClick={(e) => { e.stopPropagation(); marcarSaldada(op); }}
                              title="Marcar como saldada (aunque no haya movimiento bancario)"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="ml-1 hidden md:inline text-xs">Saldada</span>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:bg-blue-100"
                            onClick={(e) => { e.stopPropagation(); abrirEditar(op); }}
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-100"
                            onClick={(e) => { e.stopPropagation(); eliminar(op.id); }}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className={`mt-3 grid grid-cols-2 ${(op.tipo === 'click_pay' || op.tipo === 'prestamo') ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-3 text-sm`}>
                        {op.tipo === 'linea_credito' ? (
                          <>
                            <div>
                              <p className="text-gray-500">Límite</p>
                              <p className="font-semibold">{formatNumber(op.limite || 0)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Deuda</p>
                              <p className="font-semibold text-red-600">{formatNumber(r.deuda_capital || 0)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Disponible</p>
                              <p className="font-semibold text-green-600">{formatNumber(r.disponible || 0)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Coste financ.</p>
                              <p className="font-semibold text-purple-600">{formatNumber(r.coste_financiero_total || 0)}</p>
                            </div>
                          </>
                        ) : op.tipo === 'prestamo_socio' ? (
                          <>
                            <div>
                              <p className="text-gray-500">Socio</p>
                              <p className="font-semibold">{op.socio_nombre || op.entidad || '—'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Prestado</p>
                              <p className="font-semibold">{formatNumber(op.importe_solicitado || 0)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Devuelto</p>
                              <p className="font-semibold text-green-600">{formatNumber(r.capital_devuelto || 0)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Pendiente</p>
                              <p className="font-semibold text-red-600">{formatNumber(r.deuda_capital || 0)}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-gray-500">Solicitado</p>
                              <p className="font-semibold">{formatNumber(op.importe_solicitado || 0)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Cuota</p>
                              <p className="font-semibold">{formatNumber(r.importe_cuota_efectivo || op.importe_cuota || 0)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Cuotas</p>
                              <p className="font-semibold">
                                {(r.cuotas_pagadas_real ?? op.cuotas_pagadas) || 0}/{op.num_cuotas || 0}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Coste financ.</p>
                              <p className="font-semibold text-purple-600">{formatNumber(r.coste_financiero_total || 0)}</p>
                            </div>
                            <div className="col-span-2 sm:col-span-1">
                              <p className="text-gray-500">Deuda</p>
                              <p className="font-semibold text-red-600">{formatNumber(r.deuda_capital || 0)}</p>
                            </div>
                          </>
                        )}
                      </div>

                      {op.num_cuotas > 0 && (
                        <div className="mt-3">
                          <div className="w-full bg-white rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-blue-500"
                              style={{ width: `${Math.min(100, (((r.cuotas_pagadas_real ?? op.cuotas_pagadas) || 0) / op.num_cuotas) * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {expandido && (
                      <div className="border-t bg-white/60 p-4 space-y-4">
                        {/* Alertas de discrepancia detectadas en el backend */}
                        {detalle && (detalle.alertas || []).length > 0 && (
                          <div className="space-y-1">
                            {detalle.alertas.map((al, i) => {
                              const danger = al.nivel === 'danger';
                              const Icon = danger ? AlertCircle : AlertTriangle;
                              return (
                                <div
                                  key={i}
                                  className={`flex items-start gap-2 text-sm py-2 px-3 rounded border ${
                                    danger
                                      ? 'bg-red-50 border-red-200 text-red-800'
                                      : 'bg-amber-50 border-amber-200 text-amber-800'
                                  }`}
                                >
                                  <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                                  <span>{al.mensaje}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Calendario de cuotas (solo click_pay y prestamo) */}
                        {(op.tipo === 'click_pay' || op.tipo === 'prestamo') && cuotasDetalle && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> Calendario de cuotas
                            </p>
                            {cuotasDetalle.cuotas_legacy > 0 && (
                              <div className="flex items-center gap-2 text-sm py-1.5 px-2 bg-gray-100 rounded mb-1">
                                <History className="w-3 h-3 text-gray-500" />
                                <span className="text-gray-600">
                                  Cuotas pagadas anteriores (legacy): <strong>{cuotasDetalle.cuotas_legacy}</strong>
                                </span>
                              </div>
                            )}
                            {cuotasDetalle.cuotas.length === 0 ? (
                              <p className="text-sm text-gray-500">Sin cuotas futuras.</p>
                            ) : (
                              <div className="space-y-1">
                                {cuotasDetalle.cuotas.map(c => {
                                  const pagada = c.estado === 'pagada';
                                  const anulada = c.estado === 'anulada';
                                  return (
                                    <div key={c.id} className={`flex items-center justify-between text-sm py-1.5 px-2 rounded ${pagada ? 'bg-green-50' : anulada ? 'bg-gray-50 text-gray-400' : 'bg-white border border-gray-200'}`}>
                                      <div className="flex items-center gap-2 flex-1">
                                        {pagada ? (
                                          <Check className="w-3.5 h-3.5 text-green-600" />
                                        ) : anulada ? (
                                          <Ban className="w-3.5 h-3.5 text-gray-400" />
                                        ) : (
                                          <Clock className="w-3.5 h-3.5 text-orange-500" />
                                        )}
                                        <span className="font-medium">Cuota {c.numero_cuota}</span>
                                        <span className="text-gray-500">
                                          {pagada
                                            ? `pagada el ${c.fecha_pago || c.cargo?.fecha || '—'}`
                                            : anulada
                                              ? 'anulada al saldar la operación'
                                              : `prevista ${c.fecha_prevista}`}
                                        </span>
                                      </div>
                                      <span className={`font-semibold ${anulada ? 'line-through' : ''}`}>
                                        {formatNumber(pagada ? (c.importe_pagado || c.importe_previsto) : c.importe_previsto)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Vínculos bancarios */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            <Link2 className="w-3 h-3" /> Cargos bancarios vinculados
                          </p>
                          {!detalle ? (
                            <p className="text-sm text-gray-500">Cargando...</p>
                          ) : (detalle.vinculos || []).length === 0 ? (
                            <p className="text-sm text-gray-500">Sin vínculos todavía. Vincula cargos desde la pestaña &quot;Movimientos&quot;.</p>
                          ) : (
                            <div className="space-y-1">
                              {detalle.vinculos.map(v => (
                                <div key={v.id} className="flex items-center justify-between text-sm py-1 border-b border-dashed last:border-b-0">
                                  <div className="flex-1">
                                    <span className="font-medium mr-2">{TIPO_MOV_LABEL[v.tipo_movimiento] || v.tipo_movimiento}</span>
                                    <span className="text-gray-500">
                                      {v.cargo ? `${v.cargo.fecha} · ${String(v.cargo.concepto || '').slice(0, 40)}` : v.fecha}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="font-semibold">{formatNumber(v.importe_asignado)}</span>
                                    <Button variant="ghost" size="sm" className="text-red-600 h-7" onClick={() => desvincular(v.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Timeline cronológica (vínculos + cuotas pendientes) */}
                        {detalle && (detalle.timeline || []).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                              <Activity className="w-3 h-3" /> Timeline cronológica
                            </p>
                            <div className="space-y-1">
                              {detalle.timeline.map((t, i) => {
                                const pendiente = t.estado === 'pendiente';
                                const ingreso = t.signo === 'ingreso';
                                return (
                                  <div
                                    key={`${t.tipo}-${t.cargo_id || t.cuota_id || i}`}
                                    className={`flex items-center justify-between text-sm py-1.5 px-2 rounded ${
                                      pendiente
                                        ? 'bg-gray-50 border border-dashed border-gray-300'
                                        : ingreso
                                        ? 'bg-green-50'
                                        : 'bg-blue-50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <span className="text-xs text-gray-500 w-20 shrink-0">{t.fecha || '—'}</span>
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-700 shrink-0">
                                        {TIPO_MOV_LABEL[t.tipo_movimiento] || t.tipo_movimiento}
                                      </span>
                                      <span className="text-gray-600 truncate">
                                        {t.concepto || (pendiente ? '—' : '')}
                                      </span>
                                    </div>
                                    <span className={`font-semibold ml-2 ${ingreso ? 'text-green-700' : pendiente ? 'text-gray-500' : 'text-blue-700'}`}>
                                      {ingreso ? '+' : pendiente ? '' : '−'}{formatNumber(t.importe)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal nueva / editar operación */}
      <Dialog open={showNueva} onOpenChange={(open) => { if (!open) cerrarModal(); else setShowNueva(true); }}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar operación de financiación' : 'Nueva operación de financiación'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">
                Tipo de producto *
                {editingId && (
                  <span className="ml-2 text-xs text-gray-500 font-normal">
                    {(form.tipo === 'click_pay' || form.tipo === 'prestamo')
                      ? '(solo se puede cambiar entre Click&Pay y Préstamo)'
                      : '(no se puede cambiar tras crear)'}
                  </span>
                )}
              </label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {Object.entries(TIPO_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  const active = form.tipo === key;
                  const tiposIntercambiables = ['click_pay', 'prestamo'];
                  // Editando: solo dejar cambiar entre click_pay y prestamo si
                  // el tipo actual es uno de esos dos; el resto queda bloqueado.
                  const disabled = !!editingId && (
                    !tiposIntercambiables.includes(form.tipo) ||
                    !tiposIntercambiables.includes(key)
                  );
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && setForm({ ...form, tipo: key })}
                      className={`px-2 py-2 rounded-lg border-2 text-xs font-medium transition-all flex flex-col items-center gap-1 min-w-0 ${active ? `${meta.border} ${meta.bg} ${meta.color}` : 'border-gray-200 text-gray-600 hover:border-gray-300'} ${disabled && !active ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title={meta.label}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate w-full text-center leading-tight">{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Nombre *</label>
                <Input value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder={form.tipo === 'click_pay' ? 'Adelanto factura X' : 'Préstamo furgoneta'} />
              </div>
              <div>
                <label className="text-sm font-medium">Entidad</label>
                <Input value={form.entidad}
                  onChange={(e) => setForm({ ...form, entidad: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Fecha de inicio</label>
              <Input type="date" value={form.fecha_inicio}
                onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} />
            </div>

            {renderCampoEspecifico()}

            <div>
              <label className="text-sm font-medium">Notas</label>
              <Input value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                placeholder="Notas opcionales" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrarModal}>Cancelar</Button>
            <Button
              onClick={guardar}
              disabled={
                !form.nombre ||
                (form.tipo === 'linea_credito' && !form.limite) ||
                (form.tipo === 'prestamo_socio' && (!form.socio_nombre || !form.importe_solicitado)) ||
                ((form.tipo === 'click_pay' || form.tipo === 'prestamo') && (!form.importe_solicitado || !form.num_cuotas))
              }
            >
              {editingId ? 'Guardar cambios' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrestamosTab;
